import React, { useEffect, useState } from "react";
import {
    getCountries,
    getCitiesByCountry,
    getPostalCodesByCity,
    getAddressesByPostalCode,
    createOrUpdateMyClient,
} from "../../api/clientService";

const emptyForm = {
    name: "",
    lastName: "",
    dateOfBirth: "",
    selectedCountryId: "",
    selectedCityId: "",
    selectedPostalCodeId: "",
    selectedAddressId: "",
};

const ClientForm = () => {
    const [formData, setFormData] = useState(emptyForm);
    const [countries, setCountries] = useState([]);
    const [cities, setCities] = useState([]);
    const [postalCodes, setPostalCodes] = useState([]);
    const [addresses, setAddresses] = useState([]);

    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        const loadCountries = async () => {
            try {
                const data = await getCountries();
                setCountries(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error(err);
                setError("Failed to load countries.");
            }
        };

        loadCountries();
    }, []);

    useEffect(() => {
        const loadCities = async () => {
            setCities([]);
            setPostalCodes([]);
            setAddresses([]);

            setFormData((prev) => ({
                ...prev,
                selectedCityId: "",
                selectedPostalCodeId: "",
                selectedAddressId: "",
            }));

            if (!formData.selectedCountryId) return;

            try {
                const data = await getCitiesByCountry(formData.selectedCountryId);
                setCities(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error(err);
                setError("Failed to load cities.");
            }
        };

        loadCities();
    }, [formData.selectedCountryId]);

    useEffect(() => {
        const loadPostalCodes = async () => {
            setPostalCodes([]);
            setAddresses([]);

            setFormData((prev) => ({
                ...prev,
                selectedPostalCodeId: "",
                selectedAddressId: "",
            }));

            if (!formData.selectedCityId) return;

            try {
                const data = await getPostalCodesByCity(formData.selectedCityId);
                setPostalCodes(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error(err);
                setError("Failed to load postal codes.");
            }
        };

        loadPostalCodes();
    }, [formData.selectedCityId]);

    useEffect(() => {
        const loadAddresses = async () => {
            setAddresses([]);

            setFormData((prev) => ({
                ...prev,
                selectedAddressId: "",
            }));

            if (!formData.selectedPostalCodeId) return;

            try {
                const data = await getAddressesByPostalCode(formData.selectedPostalCodeId);
                setAddresses(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error(err);
                setError("Failed to load addresses.");
            }
        };

        loadAddresses();
    }, [formData.selectedPostalCodeId]);

    const handleChange = (e) => {
        setError("");
        setStatus("");

        setFormData((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const toNumberOrNull = (value) => {
        const number = Number(value);
        return Number.isFinite(number) && number > 0 ? number : null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setLoading(true);
        setStatus("");
        setError("");

        const payload = {
            name: formData.name.trim(),
            lastName: formData.lastName.trim(),
            dateOfBirth: formData.dateOfBirth || null,
            addressId: toNumberOrNull(formData.selectedAddressId),
        };
        

        console.log("CLIENT PAYLOAD:", payload);

        try {
            await createOrUpdateMyClient(payload);
            setStatus("✅ Client profile saved successfully.");
            setFormData(emptyForm);
        } catch (err) {
            console.error("Client form validation:", err.response?.data || err);

            setError(
                err?.response?.data?.message ||
                JSON.stringify(err?.response?.data) ||
                err?.message ||
                "Submission error."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <form
            onSubmit={handleSubmit}
            className="max-w-2xl mx-auto p-6 bg-white shadow rounded space-y-4"
        >
            <h2 className="text-2xl font-bold text-center">Client Form</h2>

            {status && <div className="text-green-600 text-center">{status}</div>}
            {error && <div className="text-red-600 text-center whitespace-pre-line">{error}</div>}

            <input
                name="name"
                placeholder="Name"
                value={formData.name}
                onChange={handleChange}
                className="w-full border p-2 rounded"
                required
            />

            <input
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                className="w-full border p-2 rounded"
                required
            />

            <input
                name="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={handleChange}
                className="w-full border p-2 rounded"
            />

            <select
                name="selectedCountryId"
                value={formData.selectedCountryId}
                onChange={handleChange}
                className="w-full border p-2 rounded"
                required
            >
                <option value="">-- Select Country --</option>
                {countries.map((country) => (
                    <option key={country.id} value={country.id}>
                        {country.name}
                    </option>
                ))}
            </select>

            <select
                name="selectedCityId"
                value={formData.selectedCityId}
                onChange={handleChange}
                className="w-full border p-2 rounded"
                required
                disabled={!formData.selectedCountryId}
            >
                <option value="">-- Select City --</option>
                {cities.map((city) => (
                    <option key={city.id} value={city.id}>
                        {city.name}
                    </option>
                ))}
            </select>

            <select
                name="selectedPostalCodeId"
                value={formData.selectedPostalCodeId}
                onChange={handleChange}
                className="w-full border p-2 rounded"
                required
                disabled={!formData.selectedCityId}
            >
                <option value="">-- Select Postal Code --</option>
                {postalCodes.map((postalCode) => (
                    <option key={postalCode.id} value={postalCode.id}>
                        {postalCode.code ?? postalCode.postalCode ?? postalCode.name}
                    </option>
                ))}
            </select>

            <select
                name="selectedAddressId"
                value={formData.selectedAddressId}
                onChange={handleChange}
                className="w-full border p-2 rounded"
                required
                disabled={!formData.selectedPostalCodeId}
            >
                <option value="">-- Select Address --</option>
                {addresses.map((address) => (
                    <option key={address.id} value={address.id}>
                        {address.street ?? address.addressLine ?? address.name}
                    </option>
                ))}
            </select>

            <button
                type="submit"
                className="w-full py-2 px-4 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-60"
                disabled={loading}
            >
                {loading ? "Submitting..." : "Submit"}
            </button>
        </form>
    );
};

export default ClientForm;